import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { athletesApi, statsApi } from '../lib/api';
import {
    Trophy, Users, Search, Filter, MapPin, Award,
    ArrowLeft, ChevronRight, Shield, Star, School
} from 'lucide-react';
import MaraSportsLogo from '../components/MaraSportsLogo';

const TalentDiscoveryPage = () => {
    const navigate = useNavigate();
    const [athletes, setAthletes] = useState([]);
    const [sports, setSports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        sport: '',
        country: '',
        age_category: '',
        verified: '',
        search: ''
    });

    const countries = ['Kenya', 'Ghana', 'Nigeria', 'Rwanda', 'Senegal', 'Zimbabwe', 'South Africa', 'Uganda', 'Tanzania', 'Ethiopia'];
    const ageCategories = ['U10', 'U12', 'U15', 'U18', 'U23'];

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [athletesRes, sportsRes] = await Promise.all([
                athletesApi.getAll({ limit: 50 }),
                statsApi.getSports()
            ]);
            setAthletes(athletesRes.data);
            setSports(sportsRes.data.sports);
        } catch (error) {
            console.error('Failed to load athletes:', error);
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.sport && filters.sport !== 'all') params.sport = filters.sport;
            if (filters.country && filters.country !== 'all') params.country = filters.country;
            if (filters.age_category && filters.age_category !== 'all') params.age_category = filters.age_category;
            if (filters.verified && filters.verified !== 'all') params.verified = filters.verified === 'true';
            if (filters.search) params.search = filters.search;
            
            const response = await athletesApi.getAll(params);
            setAthletes(response.data);
        } catch (error) {
            console.error('Failed to filter athletes:', error);
        } finally {
            setLoading(false);
        }
    };

    const getTopStat = (athlete) => {
        if (athlete.stats && Object.keys(athlete.stats).length > 0) {
            const sport = Object.keys(athlete.stats)[0];
            const statValues = athlete.stats[sport];
            if (statValues && Object.keys(statValues).length > 0) {
                const firstStat = Object.entries(statValues)[0];
                return { key: firstStat[0], value: firstStat[1], sport };
            }
        }
        return null;
    };

    const getTopRanking = (athlete) => {
        if (athlete.rankings && Object.keys(athlete.rankings).length > 0) {
            const entries = Object.entries(athlete.rankings);
            // Rankings values can be objects like {rank, points} or plain numbers
            const sorted = entries.sort((a, b) => {
                const valA = typeof a[1] === 'object' ? (a[1].points || a[1].rank || 0) : a[1];
                const valB = typeof b[1] === 'object' ? (b[1].points || b[1].rank || 0) : b[1];
                return valB - valA;
            });
            return sorted[0];
        }
        return null;
    };

    return (
        <div className="min-h-screen bg-[#0a1628] text-white">
            {/* Grid overlay */}
            <div className="fixed inset-0 grid-lines pointer-events-none z-0" />

            {/* Header */}
            <header className="sticky top-0 z-30 bg-[#0a1628]/80 backdrop-blur-lg border-b border-white/5">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-4">
                            <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-white">
                                <ArrowLeft className="w-4 h-4" />
                            </Link>
                            <Link to="/" className="flex items-center gap-2">
                                <MaraSportsLogo className="h-8" />
                                <span className="font-heading font-bold text-lg tracking-tight uppercase hidden sm:block">
                                    NextGen<span className="text-primary"> Tournaments</span>
                                </span>
                            </Link>
                        </div>
                        <Button
                            onClick={() => navigate('/dashboard')}
                            variant="outline"
                            className="border-white/20 text-sm"
                            data-testid="nav-dashboard"
                        >
                            Dashboard
                        </Button>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative py-16 md:py-24 border-b border-white/5">
                <div className="hero-glow absolute inset-0 z-0" />
                <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
                    <p className="text-xs tracking-widest uppercase text-accent mb-4">For Scouts & Recruiters</p>
                    <h1 className="font-heading font-black text-4xl md:text-6xl tracking-tighter uppercase mb-4">
                        Talent Discovery
                    </h1>
                    <p className="text-muted-foreground text-lg max-w-xl">
                        Search through verified athlete profiles. Filter by sport, age, country, and performance metrics. Find your next star.
                    </p>
                </div>
            </section>

            {/* Filters */}
            <section className="py-6 border-b border-white/5">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                        <div className="relative md:col-span-2">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Search by name or school..."
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                className="pl-10 bg-[#0d2847] border-white/10 h-11"
                                data-testid="talent-search"
                            />
                        </div>
                        <Select
                            value={filters.sport}
                            onValueChange={(value) => setFilters({ ...filters, sport: value })}
                        >
                            <SelectTrigger className="bg-[#0d2847] border-white/10 h-11" data-testid="filter-sport">
                                <SelectValue placeholder="All Sports" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0d2847] border-white/10">
                                <SelectItem value="all">All Sports</SelectItem>
                                {sports.map((sport) => (
                                    <SelectItem key={sport} value={sport}>{sport}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select
                            value={filters.country}
                            onValueChange={(value) => setFilters({ ...filters, country: value })}
                        >
                            <SelectTrigger className="bg-[#0d2847] border-white/10 h-11" data-testid="filter-country">
                                <SelectValue placeholder="All Countries" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0d2847] border-white/10">
                                <SelectItem value="all">All Countries</SelectItem>
                                {countries.map((country) => (
                                    <SelectItem key={country} value={country}>{country}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select
                            value={filters.age_category}
                            onValueChange={(value) => setFilters({ ...filters, age_category: value })}
                        >
                            <SelectTrigger className="bg-[#0d2847] border-white/10 h-11" data-testid="filter-age">
                                <SelectValue placeholder="All Ages" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0d2847] border-white/10">
                                <SelectItem value="all">All Ages</SelectItem>
                                {ageCategories.map((cat) => (
                                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button
                            onClick={applyFilters}
                            className="bg-accent hover:bg-amber-600 text-black h-11"
                            data-testid="apply-filters"
                        >
                            <Filter className="w-4 h-4 mr-2" />
                            Search
                        </Button>
                    </div>
                </div>
            </section>

            {/* Results */}
            <section className="py-12">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="flex items-center justify-between mb-8">
                        <p className="text-muted-foreground">
                            Found <span className="text-white font-medium">{athletes.length}</span> athletes
                        </p>
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center py-24">
                            <div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : athletes.length === 0 ? (
                        <div className="text-center py-24">
                            <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                            <h3 className="font-heading font-bold text-xl uppercase mb-2">No Athletes Found</h3>
                            <p className="text-muted-foreground">Try adjusting your filters.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {athletes.map((athlete) => {
                                const topStat = getTopStat(athlete);
                                const topRanking = getTopRanking(athlete);
                                
                                return (
                                    <Link
                                        key={athlete.id}
                                        to={`/athletes/${athlete.id}`}
                                        className="bg-[#0d2847] border border-white/5 hover:border-white/10 transition-all card-hover group"
                                        data-testid={`athlete-card-${athlete.id}`}
                                    >
                                        {/* Card Header */}
                                        <div className="p-6 border-b border-white/5">
                                            <div className="flex items-start gap-4">
                                                <div className="w-16 h-16 bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                    {athlete.profile_image ? (
                                                        <img src={athlete.profile_image} alt={athlete.full_name} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <Users className="w-8 h-8 text-primary" />
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <h3 className="font-heading font-bold text-lg uppercase tracking-tight truncate group-hover:text-primary transition-colors">
                                                            {athlete.full_name}
                                                        </h3>
                                                        {athlete.verified && (
                                                            <Shield className="w-4 h-4 text-success flex-shrink-0" />
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                        <MapPin className="w-3 h-3" />
                                                        <span>{athlete.city || athlete.country}</span>
                                                        {athlete.age_category && (
                                                            <span className="px-2 py-0.5 bg-white/5">{athlete.age_category}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Sports */}
                                        <div className="p-6 border-b border-white/5">
                                            <p className="text-xs tracking-widest uppercase text-muted-foreground mb-3">Sports</p>
                                            <div className="flex flex-wrap gap-2">
                                                {athlete.sports?.map((sport) => (
                                                    <span key={sport} className="px-3 py-1 bg-white/5 text-sm">
                                                        {sport}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Stats */}
                                        <div className="p-6 grid grid-cols-2 gap-4">
                                            {topRanking && (
                                                <div>
                                                    <p className="text-xs tracking-widest uppercase text-muted-foreground mb-1">Top Ranking</p>
                                                    <p className="stat-display text-2xl text-primary">
                                                        {typeof topRanking[1] === 'object' ? (topRanking[1].rank || topRanking[1].points || '-') : topRanking[1]}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground">{topRanking[0]}</p>
                                                </div>
                                            )}
                                            {topStat && (
                                                <div>
                                                    <p className="text-xs tracking-widest uppercase text-muted-foreground mb-1">{topStat.key}</p>
                                                    <p className="stat-display text-2xl text-accent">{topStat.value}</p>
                                                    <p className="text-xs text-muted-foreground">{topStat.sport}</p>
                                                </div>
                                            )}
                                            {!topRanking && !topStat && (
                                                <div className="col-span-2 text-center py-2">
                                                    <p className="text-xs text-muted-foreground">Stats coming soon</p>
                                                </div>
                                            )}
                                        </div>

                                        {/* School */}
                                        {athlete.school && (
                                            <div className="p-6 border-t border-white/5 flex items-center gap-2 text-sm text-muted-foreground">
                                                <School className="w-4 h-4" />
                                                <span>{athlete.school}</span>
                                            </div>
                                        )}
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default TalentDiscoveryPage;
